import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, getJson } from './client'

/**
 * SCADA operations client. Unlike the mock LAYER (src/mocks), these hit a real —
 * if simulated — backend (backend/app/api/v1/operations.py), including the SSE
 * live stream. Swapping to a real plant feed is a backend change; this stays.
 */
const BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export type Scenario = 'normal' | 'degrading' | 'incident'
export type SignalDef = { key: string; label: string; unit: string; threshold: number }
export type OpsAsset = {
  id: string
  site_id: string
  name: string
  pack: 'BESS' | 'DSEAR'
  scenario: Scenario
  signals: SignalDef[]
}
export type SignalSnapshot = {
  label: string
  unit: string
  threshold: number
  nominal: number
  history: { t: number; v: number }[]
  current: number
  projected: number
  over: boolean
  feeds: [string, string] | null
}
export type Telemetry = { asset: OpsAsset; scenario: Scenario; signals: Record<string, SignalSnapshot> }
export type Tick = {
  t: number
  scenario: Scenario
  signals: Record<string, { label: string; unit: string; v: number; threshold: number; over: boolean }>
}
export type FleetOps = { assets_streaming: number; alerts_active: number; stale_feeds: number }
export type FleetGrid = {
  assets: { id: string; name: string }[]
  signals: { key: string; label: string }[]
  grid: (number | null)[][]
}

export const useAssets = (siteId?: string) =>
  useQuery({
    queryKey: ['ops', 'assets', siteId],
    queryFn: () => getJson<OpsAsset[]>(`/operations/assets${siteId ? `?site_id=${siteId}` : ''}`),
  })

export const useTelemetry = (assetId?: string) =>
  useQuery({
    queryKey: ['ops', 'telemetry', assetId],
    queryFn: () => getJson<Telemetry>(`/operations/assets/${assetId}/telemetry`),
    enabled: !!assetId,
  })

export const useFleetOps = (siteId?: string) =>
  useQuery({
    queryKey: ['ops', 'fleet', siteId],
    queryFn: () => getJson<FleetOps>(`/operations/fleet${siteId ? `?site_id=${siteId}` : ''}`),
    refetchInterval: 5000,
  })

export const useFleetGrid = (siteId?: string) =>
  useQuery({
    queryKey: ['ops', 'grid', siteId],
    queryFn: () => getJson<FleetGrid>(`/operations/grid${siteId ? `?site_id=${siteId}` : ''}`),
    refetchInterval: 5000,
  })

/** Presenter control: set an asset's scenario (normal / degrading / incident). */
export function useSetScenario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { asset_id: string; scenario: Scenario }) =>
      api.post('/operations/scenario', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops'] }),
  })
}

export type StreamStatus = 'connecting' | 'live' | 'stale' | 'error'

/** Subscribe to the live SSE telemetry stream; reports connection health. */
export function useTelemetryStream(assetId?: string) {
  const [tick, setTick] = useState<Tick | null>(null)
  const [status, setStatus] = useState<StreamStatus>('connecting')
  const lastRef = useRef<number>(Date.now())

  useEffect(() => {
    if (!assetId) return
    setStatus('connecting')
    setTick(null)
    const es = new EventSource(`${BASE}/operations/assets/${assetId}/stream`)
    es.onmessage = (e) => {
      try {
        setTick(JSON.parse(e.data) as Tick)
        setStatus('live')
        lastRef.current = Date.now()
      } catch {
        /* ignore malformed frame */
      }
    }
    es.onerror = () => setStatus('error')
    const staleTimer = window.setInterval(() => {
      if (Date.now() - lastRef.current > 4000) setStatus((s) => (s === 'live' ? 'stale' : s))
    }, 2000)
    return () => {
      es.close()
      window.clearInterval(staleTimer)
    }
  }, [assetId])

  return { tick, status }
}
